import React, { useState, useMemo } from 'react';
import { useCurrency } from '../../contexts/CurrencyContext';
import { CURRENCIES, searchCurrencies, getCurrenciesBySupport, hasBankSyncSupport } from '../../utils/currencyConfig';
import { formatSmartCurrency } from '../../utils/formatCurrency';
import { auth } from '../../firebase';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '../../contexts/ThemeContext';

const SettingsPage = () => {
  const { currencyCode, setCurrency, symbol } = useCurrency();
  const { theme } = useTheme();
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [showCurrencyPicker, setShowCurrencyPicker] = useState(false);
  const [roundDecimals, setRoundDecimals] = useState(
    () => localStorage.getItem('forecast_round_decimals') === 'true'
  );

  const user = auth.currentUser;

  const filteredCurrencies = useMemo(() => {
    const results = searchCurrencies(searchQuery);
    // Separate Plaid-supported from manual-only
    const plaid = results.filter(c => c.plaidCountries && c.plaidCountries.length > 0);
    const manual = results.filter(c => !c.plaidCountries || c.plaidCountries.length === 0);
    return { plaid, manual };
  }, [searchQuery]);

  const handleCurrencySelect = (code) => {
    setCurrency(code);
    setShowCurrencyPicker(false);
    setSearchQuery('');
  };

  const handleToggleRoundDecimals = () => {
    const newValue = !roundDecimals;
    setRoundDecimals(newValue);
    localStorage.setItem('forecast_round_decimals', String(newValue));
  };

  const sampleAmount = 1234.56;

  return (
    <div style={{ padding: '2rem', maxWidth: 640, margin: '0 auto' }}>
      <h1 style={{ fontSize: '1.5rem', fontWeight: 700, marginBottom: '1.5rem' }}>Settings</h1>

      {/* Account section */}
      <Section theme={theme} title="Account">
        <SettingRow theme={theme} label="Email" value={user?.email || 'Not signed in'} />
        <SettingRow theme={theme}
          label="Subscription"
          value={
            <button
              onClick={() => navigate('/dashboard')}
              style={{
                background: 'none',
                border: 'none',
                color: '#2563eb',
                cursor: 'pointer',
                fontSize: '0.875rem',
                fontWeight: 500,
                padding: 0,
              }}
            >
              Manage
            </button>
          }
        />
      </Section>

      {/* Currency section */}
      <Section theme={theme} title="Currency">
        <SettingRow theme={theme}
          label="Currency"
          value={
            <button
              onClick={() => setShowCurrencyPicker(!showCurrencyPicker)}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 6,
                background: 'none',
                border: `1px solid ${theme.borderColor}`,
                borderRadius: 8,
                padding: '0.375rem 0.75rem',
                cursor: 'pointer',
                fontSize: '0.875rem',
                fontWeight: 500,
                color: theme.textColor,
              }}
            >
              <span>{symbol}</span>
              <span>{currencyCode}</span>
              <span style={{ color: theme.tertiaryText, fontSize: '0.75rem' }}>
                {showCurrencyPicker ? '▲' : '▼'}
              </span>
            </button>
          }
        />
        {hasBankSyncSupport(currencyCode) && (
          <div style={{ padding: '0.5rem 1rem', fontSize: '0.75rem', color: '#059669', display: 'flex', alignItems: 'center', gap: 4 }}>
            <span>🏦</span> Bank sync available for {currencyCode}
          </div>
        )}
      </Section>

      {/* Currency picker dropdown */}
      {showCurrencyPicker && (
        <div style={{
          border: `1px solid ${theme.borderColor}`,
          borderRadius: 12,
          backgroundColor: theme.backgroundColor,
          marginBottom: '1.5rem',
          overflow: 'hidden',
          boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
        }}>
          {/* Search */}
          <div style={{ padding: '0.75rem', borderBottom: `1px solid ${theme.lightBorder}` }}>
            <input
              type="text"
              placeholder="Search currencies or countries..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              autoFocus
              style={{
                width: '100%',
                padding: '0.625rem 0.75rem',
                border: `1px solid ${theme.borderColor}`,
                borderRadius: 8,
                fontSize: '0.875rem',
                outline: 'none',
                boxSizing: 'border-box',
              }}
            />
          </div>

          {/* Currency list */}
          <div style={{ maxHeight: 320, overflowY: 'auto' }}>
            {filteredCurrencies.plaid.length > 0 && (
              <>
                <div style={{ padding: '0.5rem 1rem', fontSize: '0.7rem', fontWeight: 600, color: theme.secondaryText, textTransform: 'uppercase', letterSpacing: '0.05em', backgroundColor: theme.hoverBg }}>
                  Bank Sync Supported
                </div>
                {filteredCurrencies.plaid.map(c => (
                  <CurrencyOption theme={theme} key={c.code} currency={c} selected={c.code === currencyCode} onSelect={handleCurrencySelect} />
                ))}
              </>
            )}
            {filteredCurrencies.manual.length > 0 && (
              <>
                <div style={{ padding: '0.5rem 1rem', fontSize: '0.7rem', fontWeight: 600, color: theme.secondaryText, textTransform: 'uppercase', letterSpacing: '0.05em', backgroundColor: theme.hoverBg }}>
                  Manual Entry
                </div>
                {filteredCurrencies.manual.map(c => (
                  <CurrencyOption theme={theme} key={c.code} currency={c} selected={c.code === currencyCode} onSelect={handleCurrencySelect} />
                ))}
              </>
            )}
          </div>
        </div>
      )}

      {/* Display section */}
      <Section theme={theme} title="Display">
        <SettingRow theme={theme}
          label="Round to whole numbers"
          value={
            <ToggleSwitch checked={roundDecimals} onChange={handleToggleRoundDecimals} />
          }
        />
        <div style={{ padding: '0.5rem 1rem', fontSize: '0.8rem', color: theme.secondaryText }}>
          Preview: {formatSmartCurrency(sampleAmount, { currencyCode, roundForDisplay: roundDecimals })}
        </div>
      </Section>

      {/* About section */}
      <Section theme={theme} title="About">
        <SettingRow theme={theme} label="Version" value="Web 1.0.0" />
        <SettingRow theme={theme}
          label="Privacy Policy"
          value={
            <a href="/privacy-policy" target="_blank" rel="noopener noreferrer" style={{ color: '#2563eb', fontSize: '0.875rem', textDecoration: 'none' }}>
              View
            </a>
          }
        />
        <SettingRow theme={theme}
          label="Terms of Service"
          value={
            <a href="/terms-of-service" target="_blank" rel="noopener noreferrer" style={{ color: '#2563eb', fontSize: '0.875rem', textDecoration: 'none' }}>
              View
            </a>
          }
        />
      </Section>
    </div>
  );
};

// Reusable section component
const Section = ({ title, children, theme }) => (
  <div style={{ marginBottom: '1.5rem' }}>
    <h2 style={{ fontSize: '0.8rem', fontWeight: 600, color: theme.secondaryText, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '0.5rem', paddingLeft: 4 }}>
      {title}
    </h2>
    <div style={{ backgroundColor: theme.backgroundColor, borderRadius: 12, border: `1px solid ${theme.borderColor}`, overflow: 'hidden' }}>
      {children}
    </div>
  </div>
);

// Reusable setting row
const SettingRow = ({ label, value, theme }) => (
  <div style={{
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '0.75rem 1rem',
    borderBottom: `1px solid ${theme.lightBorder}`,
    fontSize: '0.875rem',
  }}>
    <span style={{ color: theme.textColor }}>{label}</span>
    <span style={{ color: theme.secondaryText }}>{value}</span>
  </div>
);

// Currency option in picker
const CurrencyOption = ({ currency, selected, onSelect, theme }) => (
  <button
    onClick={() => onSelect(currency.code)}
    style={{
      display: 'flex',
      alignItems: 'center',
      width: '100%',
      padding: '0.625rem 1rem',
      border: 'none',
      borderBottom: `1px solid ${theme.lightBorder}`,
      backgroundColor: selected ? theme.activeBg : theme.backgroundColor,
      cursor: 'pointer',
      textAlign: 'left',
      gap: 10,
      transition: 'background-color 0.1s',
    }}
    onMouseEnter={(e) => { if (!selected) e.currentTarget.style.backgroundColor = theme.hoverBg; }}
    onMouseLeave={(e) => { if (!selected) e.currentTarget.style.backgroundColor = theme.backgroundColor; }}
  >
    <span style={{ fontSize: '1.125rem', minWidth: 32, textAlign: 'center', fontWeight: 600 }}>
      {currency.symbol}
    </span>
    <div style={{ flex: 1 }}>
      <div style={{ fontSize: '0.875rem', fontWeight: selected ? 600 : 400, color: selected ? theme.activeAccent : theme.textColor }}>
        {currency.name}
      </div>
      <div style={{ fontSize: '0.75rem', color: theme.tertiaryText }}>
        {currency.code}
      </div>
    </div>
    {selected && (
      <span style={{ color: theme.activeAccent, fontWeight: 700, fontSize: '1rem' }}>✓</span>
    )}
  </button>
);

// Simple toggle switch
const ToggleSwitch = ({ checked, onChange }) => (
  <button
    onClick={onChange}
    style={{
      width: 44,
      height: 24,
      borderRadius: 12,
      border: 'none',
      backgroundColor: checked ? '#2563eb' : '#d1d5db',
      cursor: 'pointer',
      position: 'relative',
      transition: 'background-color 0.2s',
      padding: 0,
    }}
  >
    <div style={{
      width: 20,
      height: 20,
      borderRadius: 10,
      backgroundColor: theme.backgroundColor,
      position: 'absolute',
      top: 2,
      left: checked ? 22 : 2,
      transition: 'left 0.2s',
      boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
    }} />
  </button>
);

export default SettingsPage;
